import { z } from 'zod';

/**
 * The approval policy of one pending voucher, as the e-mail paints its badge (#7100): `within`
 * green, `out` amber, `noQuote`/`na` neutral, and `weak` ("Política sem regra conclusiva") with
 * no badge at all.
 */
export const ApprovalPolicyZoneSchema = z.enum(['within', 'out', 'weak', 'noQuote', 'na']);
export type ApprovalPolicyZone = z.infer<typeof ApprovalPolicyZoneSchema>;

/** One time column of the card: "Saída 19:55 CNF", "Entrada ter, 10 nov". */
export const ApprovalPendingMomentSchema = z.object({
  label: z.string(),
  value: z.string(),
  extra: z.string().optional(),
});

/**
 * One pending voucher of an `approval_trip_requested`, as the e-mail lays it out on a card (#7100):
 * localized by voucher-events in the approver's language and shipped as the `pendingItemsJson`
 * var (vars are strings), in date order. Display-only: no voucher code (the Approvals screen
 * groups the items and the e-mail has one button).
 */
export const ApprovalPendingItemSchema = z.object({
  /** The voucher modal (`FLIGHT`, `HOTEL`, `EXPENSE_KM`…): picks the service icon. */
  modal: z.string(),
  /** The service and, when known, the day: "Voo · Ter, 10 nov". */
  kind: z.string(),
  policy: ApprovalPolicyZoneSchema,
  policyLabel: z.string(),
  /** Square airline mark (https), flights only. */
  thumbUrl: z.string().optional(),
  title: z.string(),
  sub: z.array(z.string()),
  moments: z.array(ApprovalPendingMomentSchema).max(2),
  /** "R$ 372,09"; null when the voucher has no amount. */
  amount: z.string().nullable(),
});
export type ApprovalPendingItem = z.infer<typeof ApprovalPendingItemSchema>;
export const ApprovalPendingItemsSchema = z.array(ApprovalPendingItemSchema).min(1);

/** Parses the `pendingItemsJson` var; null when absent, unreadable or empty (→ the e-mail of today). */
export function parseApprovalPendingItems(raw: string | undefined): ApprovalPendingItem[] | null {
  if (raw === undefined) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = ApprovalPendingItemsSchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}

/** The currency symbol `tag` prints for `currency`, or the ISO code itself when it has none. */
function currencySymbol(currency: string, tag: string): string {
  return (
    new Intl.NumberFormat(tag, { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency
  );
}

/**
 * An amount for a notification, the same in every language (#7100): the currency symbol before
 * the number with a plain space ("R$ 372,09", "US$ 10,00"), or the ISO code after it when there
 * is no symbol ("372 CLP"), with the currency's own decimals. `intlTag` picks the digits and separators; a locale that knows no
 * symbol for the currency (es-ES has none for BRL) borrows pt-BR's, the market's. An unknown
 * currency code degrades to the number and the code — the number is the point, never hide it
 * over formatting.
 */
export function formatNotificationAmount(
  amount: number,
  currency: string,
  intlTag: string,
): string {
  let symbol: string;
  // The currency's own minor units (BRL 2, JPY 0, KWD 3); an unknown code keeps two.
  let digits = 2;
  try {
    symbol = currencySymbol(currency, intlTag);
    if (symbol === currency) symbol = currencySymbol(currency, 'pt-BR');
    digits =
      new Intl.NumberFormat(intlTag, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    symbol = currency;
  }
  const number = amount.toLocaleString(intlTag, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return symbol === currency ? `${number} ${currency}` : `${symbol} ${number}`;
}
